import { z } from 'zod';
import { riskOpinionEvidenceRefSchema } from './worker-participation-api.js';
import {
  actorSnapshotSchema,
  entityIdSchema,
  nonEmptyTextSchema,
  resourceScopeSchema,
  sha256Schema,
  utcDateTimeSchema,
} from '@kop/contracts';

export const assessmentKinds = ['regular', 'ad-hoc'] as const;
export const assessmentKindSchema = z.enum(assessmentKinds);

export const assessmentStages = [
  'draft',
  'submitted',
  'in-review',
  'revision-requested',
  'resubmitted',
  'approved',
  'implementing',
  'verification-pending',
  'closed',
  'superseded',
] as const;
export const assessmentStageSchema = z.enum(assessmentStages);

export const riskScaleSnapshotSchema = z
  .object({
    scaleId: entityIdSchema,
    versionId: entityIdSchema,
    title: nonEmptyTextSchema.max(200),
    method: z.literal('likelihood-times-severity'),
    likelihood: z
      .object({ min: z.number().int().positive(), max: z.number().int().positive() })
      .strict(),
    severity: z
      .object({ min: z.number().int().positive(), max: z.number().int().positive() })
      .strict(),
    bands: z
      .array(
        z
          .object({
            key: entityIdSchema,
            label: nonEmptyTextSchema.max(100),
            minScore: z.number().int().positive(),
            maxScore: z.number().int().positive(),
            actionRule: nonEmptyTextSchema.max(500),
          })
          .strict(),
      )
      .min(1),
    sourceDocumentId: entityIdSchema,
    sourceDocumentVersion: nonEmptyTextSchema.max(100),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.likelihood.max < value.likelihood.min)
      context.addIssue({
        code: 'custom',
        path: ['likelihood'],
        message: 'Invalid likelihood range',
      });
    if (value.severity.max < value.severity.min)
      context.addIssue({ code: 'custom', path: ['severity'], message: 'Invalid severity range' });
    value.bands.forEach((band, index) => {
      if (band.maxScore < band.minScore)
        context.addIssue({ code: 'custom', path: ['bands', index], message: 'Invalid score band' });
    });
  });

/**
 * Immutable, human-approved source pin for periodic and resident-contractor ad-hoc assessments.
 * It deliberately contains no embedded legal rule, threshold or requirement: those remain in the
 * exact approved source revision identified by this snapshot.
 */
const legalBasisSourceSchema = z
  .object({
    basisId: entityIdSchema,
    versionId: entityIdSchema,
    title: nonEmptyTextSchema.max(300),
    sourceDocumentId: entityIdSchema,
    sourceRevision: nonEmptyTextSchema.max(100),
    contentHash: sha256Schema,
    scope: z
      .object({
        tenantId: entityIdSchema,
        siteId: entityIdSchema,
      })
      .strict(),
    immutable: z.literal(true),
  })
  .strict();

export const legalBasisSnapshotSchema = z.discriminatedUnion('status', [
  legalBasisSourceSchema.extend({
    status: z.literal('approved'),
    approvedAt: utcDateTimeSchema,
    approvedBy: actorSnapshotSchema,
  }).strict(),
  legalBasisSourceSchema.extend({
    status: z.literal('source-adopted'),
    adoptedAt: utcDateTimeSchema,
    adoptedBy: actorSnapshotSchema,
    adoptionReceiptId: entityIdSchema,
  }).strict(),
]);

export const riskScoreSchema = z
  .object({
    likelihood: z.number().int().positive(),
    severity: z.number().int().positive(),
    score: z.number().int().positive(),
    bandKey: entityIdSchema,
  })
  .strict();

export const riskWorkCategorySnapshotSchema = z
  .object({
    workCategoryId: entityIdSchema,
    label: nonEmptyTextSchema.max(200),
  })
  .strict();

export const riskAuthoringOrganizationSchema = z
  .object({
    organizationId: entityIdSchema,
    name: nonEmptyTextSchema.max(200),
    kind: z.enum(['site', 'contractor']),
    version: z.number().int().positive().nullable(),
    workCategories: z.array(riskWorkCategorySnapshotSchema).max(500),
  })
  .strict();

export type RiskAuthoringOrganization = z.infer<typeof riskAuthoringOrganizationSchema>;

/**
 * The evaluated organization is snapshotted independently from its mutable master.
 * Contractor-authored assessments keep contractor scope; site assessments keep site scope.
 */
export const riskAssessmentSubjectSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('site'),
      siteId: entityIdSchema,
      siteNameSnapshot: nonEmptyTextSchema.max(200),
      // Optional only for previously stored site assessments without category snapshots.
      workCategories: z.array(riskWorkCategorySnapshotSchema).max(50).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('contractor'),
      contractorId: entityIdSchema,
      contractorNameSnapshot: nonEmptyTextSchema.max(200),
      contractorVersion: z.number().int().positive(),
      workCategories: z.array(riskWorkCategorySnapshotSchema).max(50),
    })
    .strict()
    .superRefine((value, context) => {
      const ids = value.workCategories.map((item) => item.workCategoryId);
      if (new Set(ids).size !== ids.length)
        context.addIssue({
          code: 'custom',
          path: ['workCategories'],
          message: 'Selected work categories must be unique',
        });
    }),
]);

export const evidenceReferenceSchema = z
  .object({
    evidenceId: entityIdSchema,
    mediaManifestId: entityIdSchema,
    contentHash: sha256Schema,
    caption: nonEmptyTextSchema.max(500),
    capturedAt: utcDateTimeSchema,
    serverCommitted: z.boolean(),
  })
  .strict();

export const sourceReferenceSchema = z
  .object({
    sourceType: z.enum([
      'legacy-item',
      'process-standard',
      'incident',
      'change',
      'new-hazard',
      'finding',
      'tbm',
      'work-control',
    ]),
    sourceId: entityIdSchema,
    sourceVersion: nonEmptyTextSchema.max(100),
    importedAt: utcDateTimeSchema.nullable(),
  })
  .strict();

export const reductionMeasureSchema = z
  .object({
    measureId: entityIdSchema,
    description: nonEmptyTextSchema.max(2_000),
    responsibleActorId: entityIdSchema.nullable(),
    responsibleActorNameSnapshot: nonEmptyTextSchema.max(200).optional(),
    dueAt: utcDateTimeSchema.or(z.literal('')),
    status: z.enum(['planned', 'in-progress', 'implemented', 'verified', 'overdue']),
  })
  .strict();

export const riskItemSchema = z
  .object({
    itemId: entityIdSchema,
    sequence: z.number().int().positive(),
    workCategoryId: entityIdSchema.nullable().default(null),
    process: nonEmptyTextSchema.max(300),
    workStep: z.string().trim().max(500),
    hazardCategory: z.string().trim().max(200).nullable(),
    hazard: nonEmptyTextSchema.max(2_000),
    cause: z.string().trim().max(2_000).nullable(),
    /** User-entered item context. Authority comes from the record-level approved snapshot. */
    legalBasisText: z.string().trim().max(2_000).nullable().default(null),
    currentControls: z.array(nonEmptyTextSchema.max(1_000)),
    initialRisk: riskScoreSchema,
    additionalMeasures: z.array(reductionMeasureSchema),
    residualRisk: riskScoreSchema,
    sources: z.array(sourceReferenceSchema),
  })
  .strict();

const historicalRiskScoreSchema = z.object({
  likelihood: z.number().int().positive(),
  severity: z.number().int().positive(),
  score: z.number().int().positive(),
  bandKey: entityIdSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.likelihood * value.severity !== value.score)
    context.addIssue({ code: 'custom', path: ['score'], message: 'Historical stored score must equal the source product' });
});

export const historicalRiskItemSchema = riskItemSchema.omit({
  currentControls: true, initialRisk: true, additionalMeasures: true, residualRisk: true,
}).extend({
  recordKind: z.literal('historical-source-item'),
  currentControls: z.array(nonEmptyTextSchema.max(1_000)),
  initialRisk: historicalRiskScoreSchema,
  residualRisk: historicalRiskScoreSchema,
  sourceMeasureText: z.string().trim().max(2_000).nullable(),
  sourceCompletionDate: z.object({ raw: nonEmptyTextSchema.max(100), parsedOn: z.iso.date().nullable() }).strict().nullable(),
  missingSourceFields: z.array(z.enum(['currentControls', 'assessmentPeriod', 'sourceApprover', 'sourceApprovedAt'])),
  source: z.object({
    fileId: entityIdSchema, sheet: nonEmptyTextSchema.max(200), row: z.number().int().positive(),
    rowHash: sha256Schema, workbookSha256: sha256Schema,
  }).strict(),
}).strict().superRefine((value, context) => {
  if (!value.currentControls.length && !value.missingSourceFields.includes('currentControls'))
    context.addIssue({ code: 'custom', path: ['missingSourceFields'], message: 'Missing controls require source provenance' });
  if (value.currentControls.length && value.missingSourceFields.includes('currentControls'))
    context.addIssue({ code: 'custom', path: ['missingSourceFields'], message: 'Present controls cannot be marked missing' });
});

export const rosterParticipantActorSchema = z.object({
  actorKind: z.literal('roster-worker'),
  identityId: z.null(),
  workerId: entityIdSchema,
  engagementId: entityIdSchema,
  displayName: nonEmptyTextSchema.max(160),
  jobTitle: z.null(),
  organization: z.object({
    organizationId: entityIdSchema,
    organizationName: nonEmptyTextSchema.max(200),
    organizationType: z.literal('contractor'),
  }).strict(),
  sourceHash: sha256Schema,
}).strict();

export const rosterParticipantPageSchema = z.object({
  items: z.array(rosterParticipantActorSchema).max(30),
  asOf: utcDateTimeSchema,
  nextCursor: entityIdSchema.nullable(),
  heldCount: z.number().int().min(0).max(30),
}).strict();
export type RosterParticipantActor = z.infer<typeof rosterParticipantActorSchema>;
export type RosterParticipantPage = z.infer<typeof rosterParticipantPageSchema>;

export const participantSchema = z
  .object({
    participantId: entityIdSchema,
    actor: z.union([actorSnapshotSchema, rosterParticipantActorSchema]),
    contribution: z.enum([
      'hazard-identification',
      'control-review',
      'worker-consultation',
      'final-review',
    ]),
    participatedAt: utcDateTimeSchema,
    note: z.string().trim().max(1_000).nullable(),
  })
  .strict();

export const proposalSchema = z
  .object({
    proposalId: entityIdSchema,
    proposer: actorSnapshotSchema,
    submittedAt: utcDateTimeSchema,
    process: nonEmptyTextSchema.max(300),
    hazard: nonEmptyTextSchema.max(2_000),
    proposedControl: nonEmptyTextSchema.max(2_000),
    status: z.enum(['proposed', 'adopted', 'not-adopted']),
    decisionReason: z.string().trim().max(1_000).nullable(),
    adoptedItemId: entityIdSchema.nullable(),
  })
  .strict();

export const reviewRoundSchema = z
  .object({
    round: z.number().int().positive(),
    submittedAt: utcDateTimeSchema,
    submittedBy: actorSnapshotSchema,
    decidedAt: utcDateTimeSchema.nullable(),
    decidedBy: actorSnapshotSchema.nullable(),
    decision: z.enum(['pending', 'returned', 'approved', 'withdrawn']),
    opinion: z.string().trim().max(2_000).nullable(),
  })
  .strict();

export const implementationVerificationSchema = z
  .object({
    itemId: entityIdSchema,
    measureId: entityIdSchema,
    status: z.enum([
      'not-started',
      'in-progress',
      'verification-requested',
      'verified',
      'rework-required',
    ]),
    implementationNote: z.string().trim().max(2_000).nullable(),
    evidence: z.array(evidenceReferenceSchema),
    requestedAt: utcDateTimeSchema.nullable(),
    verifiedAt: utcDateTimeSchema.nullable(),
    verifiedBy: actorSnapshotSchema.nullable(),
    verificationOpinion: z.string().trim().max(2_000).nullable(),
    reassessedRisk: riskScoreSchema.nullable(),
  })
  .strict();

export const roundSummarySchema = z
  .object({
    itemCount: z.number().int().nonnegative(),
    proposalCount: z.number().int().nonnegative(),
    participantCount: z.number().int().nonnegative(),
    measureCount: z.number().int().nonnegative(),
    verifiedMeasureCount: z.number().int().nonnegative(),
    bandCounts: z.record(z.string(), z.number().int().nonnegative()),
    calculatedForVersion: z.number().int().positive(),
  })
  .strict();

export const workControlLinkSchema = z
  .object({
    caseId: entityIdSchema,
    caseRevision: z.number().int().positive(),
    workKind: z.enum(['non-routine', 'holiday', 'emergency', 'policy-high-risk']),
    importedAt: utcDateTimeSchema,
    importedStepIds: z.array(entityIdSchema).min(1),
  })
  .strict();

export const riskCarryForwardSourceSchema = z.object({
  assessmentId: entityIdSchema,
  version: z.number().int().positive(),
  contentHash: sha256Schema,
  itemBindings: z.array(z.object({ sourceItemId: entityIdSchema, itemId: entityIdSchema }).strict()).min(1).max(500),
}).strict();
export type RiskCarryForwardSource = z.infer<typeof riskCarryForwardSourceSchema>;

export const riskIncidentSourceSchema = z.object({
  entryId: entityIdSchema,
  sourceVersion: z.number().int().positive(),
  sourceHash: sha256Schema,
}).strict();
export type RiskIncidentSource = z.infer<typeof riskIncidentSourceSchema>;

export const riskIncidentLinkReceiptSchema = z.object({
  schemaVersion: z.literal(1), immutable: z.literal(true),
  tenantId: z.literal('kop'), siteId: entityIdSchema,
  assessmentId: entityIdSchema, contractorId: entityIdSchema, source: riskIncidentSourceSchema,
  reassessmentContextHash: sha256Schema,
  commandId: entityIdSchema, createdAt: utcDateTimeSchema,
}).strict();
export type RiskIncidentLinkReceipt = z.infer<typeof riskIncidentLinkReceiptSchema>;

export const riskWorkflowStepSchema = z.object({
  stepId: entityIdSchema,
  categoryId: entityIdSchema,
  order: z.number().int().positive(),
  title: nonEmptyTextSchema.max(300),
  description: z.string().max(2_000),
  itemIds: z.array(entityIdSchema).max(500),
}).strict();
export type RiskWorkflowStep = z.infer<typeof riskWorkflowStepSchema>;

export const riskAssessmentSchema = z
  .object({
    assessmentId: entityIdSchema,
    version: z.number().int().positive(),
    documentVersionId: entityIdSchema,
    scope: resourceScopeSchema,
    subject: riskAssessmentSubjectSchema.nullable().default(null),
    kind: assessmentKindSchema,
    roundId: entityIdSchema,
    title: nonEmptyTextSchema.max(300),
    assessmentPeriod: z.object({
      startsOn: z.union([z.iso.date(), z.literal('')]),
      endsOn: z.union([z.iso.date(), z.literal('')]),
    }).strict(),
    scale: riskScaleSnapshotSchema,
    legalBasisSnapshot: legalBasisSnapshotSchema.nullable().default(null),
    items: z.array(riskItemSchema).min(1),
    // Keep absence intact: adding a default changes historical approved snapshot hashes.
    workflowSteps: z.array(riskWorkflowStepSchema).max(500).optional(),
    // Absent on historical records; explicit attachment is evidence of an opinion, never an approval.
    opinionEvidenceRefs: z.array(riskOpinionEvidenceRefSchema).max(100)
      .refine(rows => new Set(rows.map(row => row.receiptId)).size === rows.length, 'Opinion receipts must be unique').optional(),
    participants: z.array(participantSchema),
    proposals: z.array(proposalSchema),
    reviewRounds: z.array(reviewRoundSchema),
    workControlLink: workControlLinkSchema.nullable(),
    carryForwardSource: riskCarryForwardSourceSchema.optional(),
    incidentSource: riskIncidentSourceSchema.optional(),
    migrationContext: z.object({
      receiptId: entityIdSchema,
      sourceGroupHash: sha256Schema,
      sourceItemCount: z.number().int().positive(),
      heldItemCount: z.number().int().nonnegative(),
      sourceCompanyName: nonEmptyTextSchema.max(200),
      sourceProcess: nonEmptyTextSchema.max(300),
      sourceRound: nonEmptyTextSchema.max(100),
      scoreInterpretation: z.literal('current-basis-original-values-preserved'),
    }).strict().optional(),
    implementation: z.array(implementationVerificationSchema),
    stage: assessmentStageSchema,
    approvalLock: z
      .object({
        contentHash: sha256Schema,
        approvedAt: utcDateTimeSchema,
        approvedBy: actorSnapshotSchema,
        approvedVersion: z.number().int().positive(),
      })
      .strict()
      .nullable(),
    roundSummary: roundSummarySchema,
    createdBy: actorSnapshotSchema,
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.incidentSource && (value.kind !== 'ad-hoc' || value.workControlLink || value.carryForwardSource))
      context.addIssue({ code: 'custom', path: ['incidentSource'], message: 'Incident reassessment must be a separate ad-hoc assessment' });
    if (!value.incidentSource) {
      if (value.subject?.workCategories && !value.subject.workCategories.length)
        context.addIssue({code:'custom',path:['subject','workCategories'],message:'A work category is required outside an incident reassessment'});
      value.items.forEach((item,index)=>{
        if(!item.currentControls.length)context.addIssue({code:'custom',path:['items',index,'currentControls'],message:'Current controls are required outside an incident reassessment'});
        item.additionalMeasures.forEach((measure,measureIndex)=>{
          if(!measure.dueAt)context.addIssue({code:'custom',path:['items',index,'additionalMeasures',measureIndex,'dueAt'],message:'A measure due date is required outside an incident reassessment'});
        });
      });
    }
    value.items.forEach((item, index) => item.additionalMeasures.forEach((measure, measureIndex) => {
      if (measure.responsibleActorId === null && !value.incidentSource)
        context.addIssue({ code: 'custom', path: ['items', index, 'additionalMeasures', measureIndex, 'responsibleActorId'], message: 'A measure owner is required outside an incident reassessment' });
    }));
    if ((!value.assessmentPeriod.startsOn || !value.assessmentPeriod.endsOn) &&
      (!value.migrationContext || value.assessmentPeriod.startsOn !== '' || value.assessmentPeriod.endsOn !== ''))
      context.addIssue({ code: 'custom', path: ['assessmentPeriod'], message: 'Exact dates are required except for a source-preserved migration draft' });
    if (value.assessmentPeriod.endsOn < value.assessmentPeriod.startsOn)
      context.addIssue({
        code: 'custom',
        path: ['assessmentPeriod'],
        message: 'Assessment period is reversed',
      });
    if (
      value.subject?.kind === 'site' &&
      (value.scope.kind === 'tenant' || value.subject.siteId !== value.scope.siteId)
    )
      context.addIssue({
        code: 'custom',
        path: ['subject', 'siteId'],
        message: 'Assessment subject must belong to the assessment site',
      });
    if (value.subject?.workCategories) {
      const selected = new Set(value.subject.workCategories.map((item) => item.workCategoryId));
      if (selected.size !== value.subject.workCategories.length)
        context.addIssue({
          code: 'custom',
          path: ['subject', 'workCategories'],
          message: 'Selected work categories must be unique',
        });
      value.items.forEach((item, index) => {
        if (!(value.incidentSource && item.workCategoryId === null && selected.size === 0) && (!item.workCategoryId || !selected.has(item.workCategoryId)))
          context.addIssue({
            code: 'custom',
            path: ['items', index, 'workCategoryId'],
            message: 'Risk items must reference a selected work category',
          });
      });
    }
    if (value.kind === 'regular' && value.workControlLink)
      context.addIssue({
        code: 'custom',
        path: ['workControlLink'],
        message: 'Regular assessments cannot be owned by a work-control case',
      });
    if (value.workControlLink && value.legalBasisSnapshot)
      context.addIssue({
        code: 'custom',
        path: ['legalBasisSnapshot'],
        message:
          'Work-control checklist assessments cannot inherit a resident or periodic legal basis',
      });
    if (
      value.legalBasisSnapshot &&
      (value.scope.kind === 'tenant' ||
        value.legalBasisSnapshot.scope.tenantId !== value.scope.tenantId ||
        value.legalBasisSnapshot.scope.siteId !== value.scope.siteId)
    )
      context.addIssue({
        code: 'custom',
        path: ['legalBasisSnapshot', 'scope'],
        message: 'Legal-basis scope must exactly match the assessment site',
      });
    if (
      value.kind === 'ad-hoc' &&
      value.workControlLink === null &&
      value.items.some((item) =>
        item.sources.some((source) => source.sourceType === 'work-control'),
      )
    )
      context.addIssue({
        code: 'custom',
        path: ['workControlLink'],
        message: 'Work-control sourced items require a case link',
      });
    if (
      ['approved', 'implementing', 'verification-pending', 'closed', 'superseded'].includes(
        value.stage,
      ) !==
      (value.approvalLock !== null)
    )
      context.addIssue({
        code: 'custom',
        path: ['approvalLock'],
        message: 'Approved lifecycle stages require an immutable approval lock',
      });
    const itemIds = new Set(value.items.map((item) => item.itemId));
    const workflowIds = new Set<string>(), workflowOrders = new Set<string>();
    const workflowCategories = new Set(value.subject?.workCategories?.map(category => category.workCategoryId) ?? []);
    const workflowItems = new Map(value.items.map(item => [item.itemId, item]));
    for (const [index, step] of (value.workflowSteps ?? []).entries()) {
      const orderKey = `${step.categoryId}:${step.order}`;
      if (workflowIds.has(step.stepId) || workflowOrders.has(orderKey))
        context.addIssue({ code: 'custom', path: ['workflowSteps', index], message: 'Workflow step IDs and category order must be unique' });
      workflowIds.add(step.stepId); workflowOrders.add(orderKey);
      if (!workflowCategories.has(step.categoryId))
        context.addIssue({ code: 'custom', path: ['workflowSteps', index, 'categoryId'], message: 'Workflow category must belong to the assessment subject' });
      if (new Set(step.itemIds).size !== step.itemIds.length)
        context.addIssue({ code: 'custom', path: ['workflowSteps', index, 'itemIds'], message: 'A workflow step cannot repeat an item' });
      for (const itemId of step.itemIds) {
        const item = workflowItems.get(itemId);
        if (!item || item.workCategoryId !== step.categoryId)
          context.addIssue({ code: 'custom', path: ['workflowSteps', index, 'itemIds'], message: 'Workflow items must exist in the same work category' });
      }
    }
    const measureKeys = new Set(
      value.items.flatMap((item) =>
        item.additionalMeasures.map((measure) => `${item.itemId}:${measure.measureId}`),
      ),
    );
    value.implementation.forEach((entry, index) => {
      if (!measureKeys.has(`${entry.itemId}:${entry.measureId}`))
        context.addIssue({
          code: 'custom',
          path: ['implementation', index],
          message: 'Implementation entry does not match a reduction measure',
        });
    });
    value.proposals.forEach((proposal, index) => {
      if (
        proposal.status === 'adopted' &&
        (!proposal.adoptedItemId || !itemIds.has(proposal.adoptedItemId))
      )
        context.addIssue({
          code: 'custom',
          path: ['proposals', index, 'adoptedItemId'],
          message: 'Adopted proposal must reference an item',
        });
    });
    value.items.forEach((item, itemIndex) => {
      [item.initialRisk, item.residualRisk].forEach((risk, riskIndex) =>
        validateRiskScore(value.scale, risk, context, [
          'items',
          itemIndex,
          riskIndex === 0 ? 'initialRisk' : 'residualRisk',
        ]),
      );
    });
    value.implementation.forEach((entry, index) => {
      if (entry.reassessedRisk)
        validateRiskScore(value.scale, entry.reassessedRisk, context, [
          'implementation',
          index,
          'reassessedRisk',
        ]);
    });
  });

function validateRiskScore(
  scale: z.infer<typeof riskScaleSnapshotSchema>,
  risk: z.infer<typeof riskScoreSchema>,
  context: z.RefinementCtx,
  path: PropertyKey[],
): void {
  if (risk.likelihood < scale.likelihood.min || risk.likelihood > scale.likelihood.max)
    context.addIssue({
      code: 'custom',
      path: [...path, 'likelihood'],
      message: 'Likelihood is outside the pinned scale',
    });
  if (risk.severity < scale.severity.min || risk.severity > scale.severity.max)
    context.addIssue({
      code: 'custom',
      path: [...path, 'severity'],
      message: 'Severity is outside the pinned scale',
    });
  if (risk.score !== risk.likelihood * risk.severity)
    context.addIssue({
      code: 'custom',
      path: [...path, 'score'],
      message: 'Score must follow the pinned multiplication method',
    });
  const band = scale.bands.find((candidate) => candidate.key === risk.bandKey);
  if (!band || risk.score < band.minScore || risk.score > band.maxScore)
    context.addIssue({
      code: 'custom',
      path: [...path, 'bandKey'],
      message: 'Band does not match the pinned scale',
    });
}

export const historicalRiskAssessmentV1Schema = z.object({
  recordKind: z.literal('historical-source-assessment'),
  assessmentId: entityIdSchema,
  version: z.literal(1),
  documentVersionId: entityIdSchema,
  scope: resourceScopeSchema.refine(value => value.kind !== 'tenant', 'Historical assessment requires site scope'),
  kind: assessmentKindSchema,
  title: nonEmptyTextSchema.max(300),
  sourceAssessmentPeriod: z.object({ raw: nonEmptyTextSchema.max(200), startsOn: z.iso.date(), endsOn: z.iso.date() }).strict().nullable(),
  items: z.array(historicalRiskItemSchema).min(1).max(500),
  historicalSource: z.object({
    receiptId: entityIdSchema,
    sourceApproved: z.literal(true),
    sourceApprover: actorSnapshotSchema.nullable(),
    sourceApprovedAt: utcDateTimeSchema.nullable(),
    contractorId: entityIdSchema,
    contractorNameSnapshot: nonEmptyTextSchema.max(200),
    workbookFileId: entityIdSchema,
    workbookSha256: sha256Schema,
    sealedMappingHash: sha256Schema,
    importedByAccount: z.email(),
    importedAt: utcDateTimeSchema,
  }).strict(),
  createdAt: utcDateTimeSchema,
  updatedAt: utcDateTimeSchema,
}).strict().superRefine((value, context) => {
  if (value.scope.kind !== 'contractor' || value.scope.contractorId !== value.historicalSource.contractorId)
    context.addIssue({ code: 'custom', path: ['scope'], message: 'Historical contractor scope must match its provenance' });
  if (value.scope.siteId !== 'site-asan')
    context.addIssue({ code: 'custom', path: ['scope', 'siteId'], message: 'Asan historical assessment requires canonical Asan scope' });
  value.items.forEach((item, index) => {
    if (item.source.fileId !== value.historicalSource.workbookFileId ||
      item.source.workbookSha256 !== value.historicalSource.workbookSha256)
      context.addIssue({ code: 'custom', path: ['items', index, 'source'], message: 'Historical item source must match the workbook provenance' });
  });
});

const historicalRiskOwnerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('contractor'), contractorId: entityIdSchema,
    organizationNameSnapshot: nonEmptyTextSchema.max(200) }).strict(),
  z.object({ kind: z.literal('site'), siteId: entityIdSchema,
    organizationNameSnapshot: nonEmptyTextSchema.max(200) }).strict(),
]);

const historicalRiskAssessmentV2Schema = z.object({
  recordKind: z.literal('historical-source-assessment'),
  historicalFormatVersion: z.literal(2),
  assessmentId: entityIdSchema,
  version: z.literal(1),
  documentVersionId: entityIdSchema,
  scope: resourceScopeSchema.refine(value => value.kind !== 'tenant', 'Historical assessment requires site scope'),
  kind: assessmentKindSchema,
  title: nonEmptyTextSchema.max(300),
  sourceAssessmentPeriod: z.object({ raw: nonEmptyTextSchema.max(200), startsOn: z.iso.date(), endsOn: z.iso.date() }).strict().nullable(),
  items: z.array(historicalRiskItemSchema).min(1).max(500),
  historicalSource: z.object({
    receiptId: entityIdSchema,
    sourceApproved: z.literal(true),
    sourceApprover: actorSnapshotSchema.nullable(),
    sourceApprovedAt: utcDateTimeSchema.nullable(),
    owner: historicalRiskOwnerSchema,
    workbookFileId: entityIdSchema,
    workbookSha256: sha256Schema,
    sealedMappingHash: sha256Schema,
    importedByAccount: z.email(),
    importedAt: utcDateTimeSchema,
  }).strict(),
  createdAt: utcDateTimeSchema,
  updatedAt: utcDateTimeSchema,
}).strict().superRefine((value, context) => {
  const owner = value.historicalSource.owner;
  if ((owner.kind === 'site' && (value.scope.kind !== 'site' || value.scope.siteId !== owner.siteId)) ||
    (owner.kind === 'contractor' && (value.scope.kind !== 'contractor' || value.scope.contractorId !== owner.contractorId)))
    context.addIssue({ code: 'custom', path: ['scope'], message: 'Historical owner must match its scope' });
  if (value.scope.siteId !== 'site-asan')
    context.addIssue({ code: 'custom', path: ['scope', 'siteId'], message: 'Asan historical assessment requires canonical Asan scope' });
  value.items.forEach((item, index) => {
    if (item.source.fileId !== value.historicalSource.workbookFileId ||
      item.source.workbookSha256 !== value.historicalSource.workbookSha256)
      context.addIssue({ code: 'custom', path: ['items', index, 'source'], message: 'Historical item source must match the workbook provenance' });
  });
});

export const historicalRiskAssessmentSchema = z.union([
  historicalRiskAssessmentV1Schema,
  historicalRiskAssessmentV2Schema,
]);

export const riskLedgerRecordSchema = z.union([riskAssessmentSchema, historicalRiskAssessmentSchema]);

export type AssessmentKind = z.infer<typeof assessmentKindSchema>;
export type AssessmentStage = z.infer<typeof assessmentStageSchema>;
export type RiskScaleSnapshot = z.infer<typeof riskScaleSnapshotSchema>;
export type LegalBasisSnapshot = z.infer<typeof legalBasisSnapshotSchema>;
export type RiskScore = z.infer<typeof riskScoreSchema>;
export type RiskWorkCategorySnapshot = z.infer<typeof riskWorkCategorySnapshotSchema>;
export type RiskAssessmentSubject = z.infer<typeof riskAssessmentSubjectSchema>;
export type RiskItem = z.infer<typeof riskItemSchema>;
export type Participant = z.infer<typeof participantSchema>;
export type Proposal = z.infer<typeof proposalSchema>;
export type EvidenceReference = z.infer<typeof evidenceReferenceSchema>;
export type RiskAssessment = z.infer<typeof riskAssessmentSchema>;
export type HistoricalRiskItem = z.infer<typeof historicalRiskItemSchema>;
export type HistoricalRiskAssessment = z.infer<typeof historicalRiskAssessmentSchema>;
export type HistoricalRiskAssessmentV1 = z.infer<typeof historicalRiskAssessmentV1Schema>;
export type RiskLedgerRecord = z.infer<typeof riskLedgerRecordSchema>;

export function historicalRiskFormatVersion(record: HistoricalRiskAssessment): 1 | 2 {
  return 'historicalFormatVersion' in record ? record.historicalFormatVersion : 1;
}

export function historicalRiskOwnerDisplay(record: HistoricalRiskAssessment) {
  if ('historicalFormatVersion' in record) {
    const owner = record.historicalSource.owner;
    return { kind: owner.kind, id: owner.kind === 'site' ? owner.siteId : owner.contractorId,
      name: owner.organizationNameSnapshot } as const;
  }
  return { kind: 'contractor' as const, id: record.historicalSource.contractorId,
    name: record.historicalSource.contractorNameSnapshot };
}

/** Hash this UTF-8 JSON with SHA-256; includes the exact parsed source version. */
export function serializeRiskCarryForwardRecord(record: unknown): string {
  const normalize = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(normalize);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right)).map(([key, child]) => [key, normalize(child)]));
    return value;
  };
  return JSON.stringify(normalize(riskAssessmentSchema.parse(record)));
}
